import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Stack,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalOffer as CouponIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { customerCouponsApi } from '../../services/smartBillApi';
import CouponReelsModal from '../../components/CouponReelsModal';
import '../../pages/Customer/CustomerDashboard.css';

const pageSize = 12;

// Removed statusOptions - using tabs instead

const UserCouponsView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Default to 'active' tab - can be overridden by URL param 'tab'
  const tabFromUrl = searchParams.get('tab');
  const [statusFilter, setStatusFilter] = useState(
    tabFromUrl && ['active', 'used', 'expired'].includes(tabFromUrl) 
      ? tabFromUrl 
      : 'active'
  );
  
  const [viewDialog, setViewDialog] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [openScratchCard, setOpenScratchCard] = useState(null);
  const [sortBy, setSortBy] = useState('assigned_date'); // assigned_date | expired_date
  const carouselRef = useRef(null);
  
  // Touch event tracking for mobile tap vs scroll
  const touchStartRef = useRef({ x: 0, y: 0, time: 0, index: -1 });
  const isScrollingRef = useRef(false);

  useEffect(() => {
    document.title = 'Coupons - LOCSY User Dashboard';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);
  
  // Ensure default tab is 'active' when component mounts (for WhatsApp links)
  useEffect(() => {
    // If no tab param in URL, ensure default is 'active'
    if (!tabFromUrl) {
      setStatusFilter('active');
    }
  }, [tabFromUrl]);

  // Use phone from query param (for admin viewing customer) or logged-in user's phone
  const phoneFromQuery = searchParams.get('phone');
  const normalizePhone = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Extract last 10 digits (in case phone includes country code like +91)
    return digits.length >= 10 ? digits.slice(-10) : digits;
  };
  const normalizedPhone = phoneFromQuery 
    ? normalizePhone(phoneFromQuery)
    : normalizePhone(user?.phone || '');

  const { data, isLoading, error, refetch } = useQuery(
    ['user-smart-coupons', normalizedPhone, lastRefresh],
    async () => {
      if (!normalizedPhone) {
        return { coupons: [], summary: { total: 0, active: 0, used: 0, expired: 0 } };
      }
      return customerCouponsApi.getByPhone(normalizedPhone);
    },
    {
      enabled: !!normalizedPhone,
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
    }
  );

  const coupons = data?.coupons || [];

  const filteredCoupons = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = coupons.filter((coupon) => {
      // Filter by status based on selected tab
      let matchesStatus = false;
      const couponStatus = coupon.status || 'active';
      const validUntil = coupon.valid_until || coupon.validUntil;
      const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
      
      if (statusFilter === 'active') {
        // Active: status is 'active' and not expired
        matchesStatus = couponStatus === 'active' && !isExpired;
      } else if (statusFilter === 'used') {
        // Used: status is 'used'
        matchesStatus = couponStatus === 'used';
      } else if (statusFilter === 'expired') {
        // Expired: status is 'expired' or valid_until is past
        matchesStatus = couponStatus === 'expired' || isExpired;
      }
      
      const mitraName = (
        coupon.mitra_name ||
        coupon.shop_name ||
        coupon.partner_id ||
        ''
      ).toString().toLowerCase();

      const matchesSearch =
        !query ||
        coupon.name?.toLowerCase().includes(query) ||
        coupon.code?.toLowerCase().includes(query) ||
        coupon.service?.toLowerCase().includes(query) ||
        coupon.service_name?.toLowerCase().includes(query) ||
        coupon.coupon_name?.toLowerCase().includes(query) ||
        mitraName.includes(query);

      return matchesStatus && matchesSearch;
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'expired_date') {
        const ae = new Date(a.valid_until || a.validUntil || 0).getTime();
        const be = new Date(b.valid_until || b.validUntil || 0).getTime();
        return ae - be;
      }
      const ai = new Date(
        a.generated_at || a.issued_at || a.issued_date || a.created_at || 0,
      ).getTime();
      const bi = new Date(
        b.generated_at || b.issued_at || b.issued_date || b.created_at || 0,
      ).getTime();
      return bi - ai;
    });

    return list;
  }, [coupons, search, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredCoupons.length / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedCoupons = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredCoupons.slice(start, start + pageSize);
  }, [filteredCoupons, page]);

  const handleRefresh = () => {
    if (!normalizedPhone) {
      toast.error('Phone number missing. Please login again.');
      return;
    }
    setLastRefresh(new Date());
    refetch();
    toast.info('Coupons refreshed');
  };

  const handleCopyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(`Coupon code "${code}" copied to clipboard!`);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getAmountDetails = (coupon) => {
    const original = Number(coupon.original_price) || 0;
    const final = Number(coupon.final_price) || 0;
    const discountAmount =
      Number(coupon.discount_amount) || Math.max(original - final, 0);
    return { original, final, discountAmount };
  };

  // Cache for day-wise pricing calculations (performance optimization)
  const pricingCacheRef = useRef(new Map());

  // Calculate day-wise pricing with dates - Optimized with caching
  const getDayWisePricing = (coupon) => {
    // Debug: Log coupon data to see what fields are available
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Checking coupon for day-wise pricing:', {
        id: coupon.id,
        name: coupon.name,
        has_day_wise_pricing: !!coupon.day_wise_pricing,
        day_wise_pricing: coupon.day_wise_pricing,
        offer_type: coupon.offer_type,
        offerType: coupon.offerType,
        allKeys: Object.keys(coupon)
      });
    }

    // Check for day_wise_pricing field (can be snake_case or camelCase)
    const dayWisePricing = coupon.day_wise_pricing || coupon.dayWisePricing;
    const offerType = coupon.offer_type || coupon.offerType;
    
    // If no day_wise_pricing data, return null
    if (!dayWisePricing) {
      return null;
    }

    // Check if it's an increasing offer type (optional check - show pricing if data exists)
    // if (offerType && offerType !== 'increasing') {
    //   return null;
    // }

    // Use cache key based on coupon ID and pricing data
    const cacheKey = `${coupon.id}_${JSON.stringify(dayWisePricing)}_${coupon.generated_at || coupon.created_at}`;
    const cache = pricingCacheRef.current;
    
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    try {
      const dayPricing = typeof dayWisePricing === 'string' 
        ? JSON.parse(dayWisePricing) 
        : dayWisePricing;
      
      if (!dayPricing || typeof dayPricing !== 'object' || Object.keys(dayPricing).length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ Invalid day_wise_pricing format:', dayPricing);
        }
        cache.set(cacheKey, null);
        return null;
      }

      // Get start date (generated_at, created_at, or start_date)
      const startDate = coupon.generated_at 
        ? new Date(coupon.generated_at)
        : coupon.created_at 
        ? new Date(coupon.created_at)
        : coupon.start_date 
        ? new Date(coupon.start_date)
        : new Date();

      // Get validity days
      const validityDays = Number(coupon.validity_days) || 30;
      const endDate = coupon.valid_until 
        ? new Date(coupon.valid_until)
        : new Date(startDate.getTime() + (validityDays - 1) * 24 * 60 * 60 * 1000);

      // Sort days and create date-price mapping
      const sortedDays = Object.keys(dayPricing)
        .map(Number)
        .filter(d => d > 0 && d <= validityDays)
        .sort((a, b) => a - b);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const pricingDays = sortedDays.map(dayNum => {
        const date = new Date(startDate);
        date.setDate(date.getDate() + (dayNum - 1));
        date.setHours(0, 0, 0, 0);
        
        const todayStr = today.toDateString();
        const dateStr = date.toDateString();
        
        return {
          day: dayNum,
          date: date,
          price: Number(dayPricing[dayNum.toString()]) || 0,
          isToday: dateStr === todayStr,
          isPast: date < today && dateStr !== todayStr,
          isFuture: date > today
        };
      });

      const result = {
        pricingDays,
        startDate,
        endDate,
        totalDays: validityDays
      };
      
      cache.set(cacheKey, result);
      // Limit cache size to prevent memory issues
      if (cache.size > 100) {
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing day-wise pricing:', error);
      cache.set(cacheKey, null);
      return null;
    }
  };

  // Color cache for performance
  const colorCacheRef = useRef(new Map());

  // Get color for day based on position (green to red gradient) - Optimized with caching
  const getDayColor = (index, total) => {
    const cacheKey = `${index}_${total}`;
    const colorCache = colorCacheRef.current;
    
    if (colorCache.has(cacheKey)) {
      return colorCache.get(cacheKey);
    }
    
    if (total === 1) {
      colorCache.set(cacheKey, '#4caf50');
      return '#4caf50'; // Green
    }
    
    const ratio = index / (total - 1);
    
    // Green (0, 255, 0) to Red (255, 0, 0)
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio));
    const b = 0;
    
    const color = `rgb(${r}, ${g}, ${b})`;
    colorCache.set(cacheKey, color);
    return color;
  };

  const getStatusChip = (coupon) => {
    switch (coupon.status) {
      case 'used':
        return { label: 'Used', color: 'warning' };
      case 'expired':
        return { label: 'Expired', color: 'error' };
      default:
        return { label: 'Active', color: 'success' };
    }
  };

  const getCouponAmount = (coupon) => {
    if (coupon.offer_price) return Number(coupon.offer_price);
    if (coupon.final_price) return Number(coupon.final_price);
    if (coupon.actual_price) return Number(coupon.actual_price);
    return 0;
  };

  const formatCurrency = (value) => {
    const number = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    if (!carouselRef.current || filteredCoupons.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            const card = entry.target;
            const starsContainer = card.querySelector('.floating-stars');
            const flowersContainer = card.querySelector('.floating-flowers');
            if (starsContainer) starsContainer.classList.add('animate');
            if (flowersContainer) flowersContainer.classList.add('animate');
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    const cards = carouselRef.current?.querySelectorAll('.carousel-item');
    if (cards) {
      cards.forEach((card) => observer.observe(card));
    }

    return () => {
      if (cards) {
        cards.forEach((card) => observer.unobserve(card));
      }
    };
  }, [filteredCoupons]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load coupons. Please try again.</Alert>;
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          flexWrap: 'wrap',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold" 
            sx={{ 
              mb: 0.5,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              wordBreak: 'break-word',
            }}
          >
            {phoneFromQuery ? `Customer Coupons (${phoneFromQuery})` : 'Available Coupons'}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {phoneFromQuery 
              ? `Viewing coupons for customer: ${phoneFromQuery}`
              : 'Scratch to unlock, then Redeem to send an order to the shop (same as Locsy app).'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, maxWidth: 560 }}
          >
            At the shop, applying a coupon depends on the Mitra&apos;s willingness — they may
            honour, edit, or refuse. Locsy is not liable for coupon disputes. Coupon is marked
            used only after the shop confirms payment.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/user/orders')}
          >
            My orders
          </Button>
          <IconButton 
            onClick={handleRefresh} 
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Filters */}
      <Card elevation={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search shop / mitra, coupon, or code..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="assigned_date">Newest issued</MenuItem>
                  <MenuItem value="expired_date">Expiry soon</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={statusFilter}
                onChange={(e, newValue) => {
                  setStatusFilter(newValue);
                  setPage(1); // Reset to first page when switching tabs
                }}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minWidth: { xs: 80, sm: 120 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                  },
                }}
              >
                <Tab 
                  label={`Active (${coupons.filter(c => {
                    const status = c.status || 'active';
                    const validUntil = c.valid_until || c.validUntil;
                    const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
                    return status === 'active' && !isExpired;
                  }).length})`}
                  value="active"
                />
                <Tab 
                  label={`Used (${coupons.filter(c => (c.status || 'active') === 'used').length})`}
                  value="used"
                />
                <Tab 
                  label={`Expired (${coupons.filter(c => {
                    const status = c.status || 'active';
                    const validUntil = c.valid_until || c.validUntil;
                    const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
                    return status === 'expired' || isExpired;
                  }).length})`}
                  value="expired"
                />
              </Tabs>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Total: {data?.summary?.total ?? coupons.length}
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Active:{' '}
                  {data?.summary?.active ??
                    coupons.filter((c) => (c.status || 'active') === 'active').length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Coupons Carousel - Scratch Card Design */}
      {filteredCoupons.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <CouponIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No coupons match your filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting the search or status filters to view other coupons.
          </Typography>
        </Paper>
      ) : (
        <div className="coupons-carousel-wrapper" style={{ marginTop: '24px' }}>
          <div className="coupons-carousel" ref={carouselRef}>
            {paginatedCoupons.map((coupon, index) => {
          const statusChip = getStatusChip(coupon);
          const { original, final, discountAmount } = getAmountDetails(coupon);
          const dayWisePricing = getDayWisePricing(coupon);
          
          // Debug log in development
          if (process.env.NODE_ENV === 'development' && coupon.day_wise_pricing) {
            console.log('📊 Coupon with day_wise_pricing:', {
              id: coupon.id,
              name: coupon.name,
              day_wise_pricing: coupon.day_wise_pricing,
              offer_type: coupon.offer_type,
              calculated: dayWisePricing
            });
          }

          const isUsed = coupon.status === 'used';
          const validUntil = coupon.valid_until || coupon.validUntil;
          const isExpired = validUntil ? new Date(validUntil) < new Date() : false;
          
          // Format issued date/time: "CODE - DD MMM YY HH:MM"
          const formatIssuedDateTime = (date) => {
            if (!date) return '';
            try {
              const d = new Date(date);
              if (isNaN(d.getTime())) return '';
              const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
              const day = String(d.getDate()).padStart(2, '0');
              const month = months[d.getMonth()];
              const year = String(d.getFullYear()).substring(2);
              const hour = String(d.getHours()).padStart(2, '0');
              const minute = String(d.getMinutes()).padStart(2, '0');
              return `${day} ${month} ${year} ${hour}:${minute}`;
            } catch (e) {
              console.warn('Error formatting date:', date, e);
              return '';
            }
          };
          
          // Check multiple possible date field names
          const issuedDate = coupon.generated_at || coupon.created_at || coupon.issued_at || coupon.issue_date || coupon.date_issued;
          const issuedDateTimeStr = issuedDate ? formatIssuedDateTime(issuedDate) : '';
          const couponCode = coupon.code || coupon.coupon_code || 'N/A';
          const issuedDisplay = issuedDateTimeStr ? `${couponCode}- ${issuedDateTimeStr}` : `${couponCode}- N/A`;
          
          // Debug log in development
          if (process.env.NODE_ENV === 'development' && !issuedDateTimeStr) {
            console.log('⚠️ No issued date found for coupon:', {
              id: coupon.id,
              code: couponCode,
              generated_at: coupon.generated_at,
              created_at: coupon.created_at,
              issued_at: coupon.issued_at,
              allKeys: Object.keys(coupon).filter(k => k.includes('date') || k.includes('at') || k.includes('time'))
            });
          }

          // Generate unique color for each coupon - expanded color palette
          const colorSchemes = [
            { primary: '#6366f1', secondary: '#8b5cf6', accent: '#a5b4fc', name: 'Indigo' },
            { primary: '#ec4899', secondary: '#f472b6', accent: '#fbcfe8', name: 'Pink' },
            { primary: '#10b981', secondary: '#34d399', accent: '#6ee7b7', name: 'Green' },
            { primary: '#f59e0b', secondary: '#fbbf24', accent: '#fde68a', name: 'Amber' },
            { primary: '#3b82f6', secondary: '#60a5fa', accent: '#93c5fd', name: 'Blue' },
            { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#c4b5fd', name: 'Purple' },
            { primary: '#ef4444', secondary: '#f87171', accent: '#fca5a5', name: 'Red' },
            { primary: '#14b8a6', secondary: '#5eead4', accent: '#99f6e4', name: 'Teal' },
            { primary: '#f97316', secondary: '#fb923c', accent: '#fdba74', name: 'Orange' },
            { primary: '#06b6d4', secondary: '#22d3ee', accent: '#67e8f9', name: 'Cyan' },
            { primary: '#a855f7', secondary: '#c084fc', accent: '#d8b4fe', name: 'Violet' },
            { primary: '#84cc16', secondary: '#a3e635', accent: '#bef264', name: 'Lime' },
            { primary: '#eab308', secondary: '#facc15', accent: '#fde047', name: 'Yellow' },
            { primary: '#06b6d4', secondary: '#0891b2', accent: '#22d3ee', name: 'Sky' },
            { primary: '#d946ef', secondary: '#e879f9', accent: '#f0abfc', name: 'Fuchsia' },
            { primary: '#22c55e', secondary: '#4ade80', accent: '#86efac', name: 'Emerald' },
          ];
          // Use coupon ID or name hash to determine color
          const couponId = coupon.id || coupon.name || index;
          const hashValue = typeof couponId === 'string' 
            ? couponId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
            : couponId;
          const colorIndex = Math.abs(hashValue) % colorSchemes.length;
          const colorScheme = colorSchemes[colorIndex] || colorSchemes[0];

          // Touch event handlers for mobile
          const handleTouchStart = (e) => {
            const touch = e.touches[0];
            touchStartRef.current = {
              x: touch.clientX,
              y: touch.clientY,
              time: Date.now(),
              index: index,
            };
            isScrollingRef.current = false;
          };

          const handleTouchMove = (e) => {
            if (touchStartRef.current.index !== index) return;
            if (!touchStartRef.current.x && !touchStartRef.current.y) return;
            
            const touch = e.touches[0];
            const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
            const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
            
            if (deltaX > 10 || (deltaX > 5 && deltaX > deltaY)) {
              isScrollingRef.current = true;
            }
          };

          const handleTouchEnd = (e) => {
            if (touchStartRef.current.index !== index) return;
            if (!touchStartRef.current.x && !touchStartRef.current.y) return;
            
            const touch = e.changedTouches[0];
            const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
            const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
            const deltaTime = Date.now() - touchStartRef.current.time;
            const wasScrolling = isScrollingRef.current;
            
            touchStartRef.current = { x: 0, y: 0, time: 0, index: -1 };
            isScrollingRef.current = false;
            
            if (!wasScrolling && deltaX < 10 && deltaY < 10 && deltaTime < 300) {
              // Allow viewing all coupons (used, expired, active) - read-only mode
              e.preventDefault();
              e.stopPropagation();
              setOpenScratchCard(index);
            }
          };

          const handleClick = (e) => {
            // Prevent double-tap on mobile
            if (touchStartRef.current.index === index) {
              return;
            }
            // Allow viewing all coupons (used, expired, active) - read-only mode
            setOpenScratchCard(index);
          };

                          return (
            <React.Fragment key={`coupon-${coupon.id}-${coupon.status || 'active'}`}>
              <div 
                className={`coupon-card carousel-item ${isUsed ? 'used-coupon' : ''} ${isExpired ? 'expired-coupon' : ''} ${openScratchCard === index ? 'active' : ''}`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  ...(isUsed 
                    ? { opacity: 0.8, backgroundColor: '#f5f5f5', border: '2px solid #9ca3af' }
                    : isExpired
                    ? { opacity: 0.8, backgroundColor: '#fef2f2', border: '2px solid #fca5a5' }
                    : { 
                        border: '1px solid rgba(255, 107, 0, 0.35)',
                        boxShadow: '0 8px 20px rgba(255, 107, 0, 0.12)'
                      })
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={handleClick}
              >
                {/* Floating decorative text */}
                {!isUsed && !isExpired && (
                  <>
                    <div className="floating-text floating-text-1">COUPONS</div>
                    <div className="floating-text floating-text-2">KNLS</div>
                    <div className="floating-text floating-text-3">OFFERS</div>
                    <div className="floating-stars">
                      {[...Array(8)].map((_, i) => (
                        <span key={i} className="star-emoji" style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 2}s`
                        }}>⭐</span>
                      ))}
                    </div>
                    <div className="floating-flowers">
                      {[...Array(6)].map((_, i) => (
                        <span key={i} className="flower-emoji" style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 2}s`
                        }}>🌸</span>
                      ))}
                    </div>
                  </>
                )}
                <div 
                  className="coupon-header"
                  style={{ 
                    background: isUsed 
                      ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                      : isExpired
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : 'linear-gradient(135deg, #ff6b00 0%, #ff8c38 55%, #ffb347 100%)'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h3 style={isUsed || isExpired ? { color: '#fff' } : {}}>
                      {coupon.service || coupon.service_name || coupon.name || coupon.coupon_name || 'Coupon'}
                    </h3>
                    {(coupon.mitra_name || coupon.shop_name) && (
                      <div style={{
                        marginTop: '2px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: isUsed || isExpired ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.95)',
                      }}>
                        🏪 {coupon.mitra_name || coupon.shop_name}
                      </div>
                    )}
                    <div style={{ 
                      marginTop: '4px',
                      fontSize: '11px', 
                      color: isUsed || isExpired ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.85)',
                      fontWeight: '500',
                      fontFamily: 'monospace',
                      letterSpacing: '0.5px'
                    }}>
                      {issuedDisplay}
                    </div>
                    {coupon.scratched_at && (
                      <div style={{
                        marginTop: '2px',
                        fontSize: '10px',
                        color: isUsed || isExpired ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.8)',
                      }}>
                        Scratched {formatIssuedDateTime(coupon.scratched_at)}
                      </div>
                    )}
                  </div>
                  <span className={`coupon-status ${isUsed ? 'used' : isExpired ? 'expired' : 'active'}`}>
                    {isUsed ? 'Used' : isExpired ? 'Expired' : 'Active'}
                  </span>
                </div>
                <div 
                  className="coupon-preview"
                >
                  {isUsed ? (
                    <div className="coupon-body-preview">
                      <div className="coupon-service">
                        <i className="fas fa-tag"></i> {coupon.service_name || coupon.service || 'Service'}
                      </div>
                      <div className="coupon-code-preview">
                        <div className="code-display">{coupon.code || coupon.coupon_code || 'N/A'}</div>
                        <div className="code-label">Already Used</div>
                      </div>
                    </div>
                  ) : isExpired ? (
                    <div className="coupon-body-preview">
                      <div className="coupon-service">
                        <i className="fas fa-tag"></i> {coupon.service_name || coupon.service || 'Service'}
                      </div>
                      <div className="coupon-code-preview">
                        <div className="code-display expired-code">{coupon.code || coupon.coupon_code || 'N/A'}</div>
                        <div className="code-label expired-label">Expired</div>
                      </div>
                    </div>
                      ) : (
                    <div 
                      className="scratch-preview"
                    >
                      <div className="scratch-preview-content">
                        <i className="fas fa-hand-pointer"></i>
                        <h4>SCRATCH TO UNLOCK</h4>
                        <p>Tap to reveal like Google Pay</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="coupon-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!isUsed && !isExpired && (
                    <button
                      className="btn btn-success btn-claim"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/user/redeem', { state: { coupon } });
                      }}
                      style={{ backgroundColor: '#FF6A00', borderColor: '#FF6A00' }}
                    >
                      <i className="fas fa-shopping-bag"></i> Redeem → order
                    </button>
                  )}
                  <button
                    className="btn btn-success btn-claim"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyCode(coupon.code || coupon.coupon_code);
                      if (isUsed) {
                        toast.info('Coupon code copied (already used)');
                      } else if (isExpired) {
                        toast.info('Coupon code copied (expired)');
                      } else {
                        toast.info('Code copied — or use Redeem to place an order online');
                      }
                    }}
                    style={
                      isUsed || isExpired 
                        ? { opacity: 0.7, cursor: 'pointer', backgroundColor: '#9ca3af' } 
                        : { backgroundColor: '#64748b', borderColor: '#64748b' }
                    }
                  >
                    <i className="fas fa-copy"></i> {isUsed ? 'Copy (Used)' : isExpired ? 'Copy (Expired)' : 'Copy code'}
                  </button>
                </div>
              </div>
            </React.Fragment>
          );
        })}
          </div>
        </div>
      )}

      {/* Empty State - moved above */}
      {/* Removed duplicate empty state */}

      {/* Pagination */}
      {filteredCoupons.length > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, sm: 4 } }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
            size={isMobile ? 'small' : 'medium'}
            siblingCount={isMobile ? 0 : 1}
          />
        </Box>
      )}

      {/* Coupon Reels Modal - Instagram Reels style - Read-only for users */}
      <CouponReelsModal
        coupons={paginatedCoupons}
        initialIndex={typeof openScratchCard === 'number' ? openScratchCard : 0}
        isOpen={typeof openScratchCard === 'number'}
        primaryAction="redeem"
        onClose={() => {
          // Reset state properly to prevent cards from disappearing
          setOpenScratchCard(null);
          // Small delay to ensure state updates properly
          setTimeout(() => {
            // Force re-render by updating lastRefresh slightly
            setLastRefresh(new Date());
          }, 100);
        }}
        onClaim={(fromReel) => {
          const currentCoupon =
            fromReel ||
            paginatedCoupons[typeof openScratchCard === 'number' ? openScratchCard : 0];
          setOpenScratchCard(null);
          if (!currentCoupon) return;
          const used = (currentCoupon.status || '').toLowerCase() === 'used';
          const expired =
            currentCoupon.valid_until &&
            new Date(currentCoupon.valid_until) < new Date() &&
            !used;
          if (used || expired) {
            handleCopyCode(currentCoupon.code || currentCoupon.coupon_code);
            toast.info(used ? 'Already used' : 'Expired');
            return;
          }
          navigate('/user/redeem', { state: { coupon: currentCoupon } });
        }}
        onUtilizeLater={() => {
          toast.info('Coupon saved for later use', { autoClose: 2000 });
        }}
        formatCurrency={formatCurrency}
        getCouponAmount={getCouponAmount}
        getDayWisePricing={getDayWisePricing}
        phone={normalizedPhone}
        customerName={user?.name || ''}
        navigate={(path) => window.location.href = path}
      />

      {/* View Details Dialog - keeping for backward compatibility */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CouponIcon color="primary" />
            <Typography variant="h6">{viewDialog?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewDialog && (() => {
            const dialogDayWisePricing = getDayWisePricing(viewDialog);
            return (
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#1976d2',
                  borderRadius: 2,
                  textAlign: 'center',
                  color: '#ffffff',
                }}
              >
                <Typography 
                  variant="h5" 
                  fontWeight={700} 
                  sx={{ 
                    fontSize: { xs: '1.3rem', sm: '1.6rem' },
                    mb: 2,
                  }}
                >
                  {viewDialog.service || viewDialog.name}
                </Typography>
                
                <Typography 
                  variant="h3" 
                  fontWeight={900} 
                  sx={{ 
                    fontSize: { xs: '2.5rem', sm: '3rem' },
                    color: '#4caf50',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    mb: 1,
                  }}
                >
                  Save ₹{Number(viewDialog.discount_amount || 0).toFixed(0)}
                </Typography>
                
                <Typography 
                  variant="h6" 
                  fontWeight={700} 
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.3rem' },
                    color: '#f44336',
                    textDecoration: 'line-through',
                    mb: 0.5,
                  }}
                >
                  ₹{Number(viewDialog.original_price || 0).toFixed(0)}
                </Typography>
                
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  sx={{ 
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    color: '#ffeb3b',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    mb: 2,
                  }}
                >
                  ₹{Number(viewDialog.final_price || 0).toFixed(0)}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  fontFamily="monospace" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 600,
                  }}
                >
                  {viewDialog.code}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Service
                  </Typography>
                  <Typography variant="body2">{viewDialog.service || 'Any'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Status
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {viewDialog.status || 'active'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Valid Until
                  </Typography>
                  <Typography variant="body2">
                    {viewDialog.valid_until
                      ? format(new Date(viewDialog.valid_until), 'MMM dd, yyyy')
                      : 'No expiry'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Savings
                  </Typography>
                  <Typography variant="body2">
                    Save ₹{Number(viewDialog.discount_amount || 0).toFixed(0)}
                  </Typography>
                </Grid>
                {(viewDialog.generated_at || viewDialog.created_at) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Generated On
                    </Typography>
                    <Typography variant="body2">
                      {format(
                        new Date(viewDialog.generated_at || viewDialog.created_at), 
                        'MMM dd, yyyy • hh:mm a'
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              {/* Day-wise Pricing Section */}
              {dialogDayWisePricing && dialogDayWisePricing.pricingDays && dialogDayWisePricing.pricingDays.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="primary" />
                    Day-wise Offer Prices
                  </Typography>
                  <Grid container spacing={2}>
                    {dialogDayWisePricing.pricingDays.map((dayInfo, idx) => {
                      const totalDays = dialogDayWisePricing.pricingDays.length;
                      const dayColor = getDayColor(idx, totalDays);
                      return (
                        <Grid item xs={6} sm={4} md={3} key={dayInfo.day}>
                          <Paper
                            elevation={dayInfo.isToday ? 4 : 1}
                            sx={{
                              p: 2,
                              borderLeft: `4px solid ${dayColor}`,
                              bgcolor: dayInfo.isToday ? 'warning.light' : 'background.paper',
                              position: 'relative',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3,
                              },
                            }}
                          >
                            {dayInfo.isToday && (
                              <Chip
                                label="Today"
                                size="small"
                                color="warning"
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  fontSize: '0.65rem',
                                  height: 20,
                                }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                              Day {dayInfo.day}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1 }}>
                              {dayInfo.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ color: dayColor }}
                            >
                              {formatCurrency(dayInfo.price)}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
          {viewDialog?.code && viewDialog?.status !== 'used' && viewDialog?.status !== 'expired' && (
            <Button
              variant="contained"
              onClick={() => {
                handleCopyCode(viewDialog.code);
                toast.success('Coupon code copied! Ready to claim.');
                setViewDialog(null);
              }}
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(45deg, #4caf50 30%, #45a049 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #45a049 30%, #3d8b40 90%)',
                },
              }}
            >
              QUICK CLAIM
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserCouponsView;


